import { MoneyScaled } from '@quickbit/qb-design-system';

export enum TransactionType {
  BUY = 'BUY',
  SELL = 'SELL',
  SEND_INTERNAL = 'SEND_INTERNAL',
  SEND_EXTERNAL = 'SEND_EXTERNAL',
  RECEIVE_INTERNAL = 'RECEIVE_INTERNAL',
  RECEIVE_EXTERNAL = 'RECEIVE_EXTERNAL',
  SWAP_SOURCE = 'SWAP_SOURCE',
  SWAP_DESTINATION = 'SWAP_DESTINATION',
  REFUND = 'REFUND',
  SEND_FIAT_INTERNAL = 'SEND_FIAT_INTERNAL',
  SEND_FIAT_EXTERNAL = 'SEND_FIAT_EXTERNAL',
  RECEIVE_FIAT_INTERNAL = 'RECEIVE_FIAT_INTERNAL',
  RECEIVE_FIAT_EXTERNAL = 'RECEIVE_FIAT_EXTERNAL',
  CARD_PAYMENT = 'CARD_PAYMENT',
  INTERNAL_TRANSFER_SOURCE = 'INTERNAL_TRANSFER_SOURCE',
  INTERNAL_TRANSFER_DESTINATION = 'INTERNAL_TRANSFER_DESTINATION',
  YIELD_PAYOUT = 'YIELD_PAYOUT',
  GIVEAWAY = 'GIVEAWAY',
}

export interface TransactionMinimal {
  id: string;
  type: TransactionType;
  supportId: string;
  walletId: string;
  amount: MoneyScaled;
  amountReference: MoneyScaled;
  createdAt: Date;
  updatedAt: Date;
  status: string;
  exchangeRatesId: number;
  euroAmount: MoneyScaled;
  serviceFee: MoneyScaled;
  referenceFee: MoneyScaled;
  totalReference: MoneyScaled;
  nexusTransactionCode?: string;
  balanceAtTransactionTime?: MoneyScaled;
  balanceReferenceAtTransactionTime?: MoneyScaled;
  balanceAfterTransaction?: MoneyScaled;
  balanceReferenceAfterTransaction?: MoneyScaled;
  blockchainTransactionId?: string;
  authorizedBy?: {
    email: string;
  };
}

export interface TransactionBase extends TransactionMinimal {
  type: TransactionType;
  exchangeRateReference: MoneyScaled;
}

type CardBrand = 'VISA' | 'AMEX' | 'MASTERCARD' | 'UNKNOWN';

export interface MaskedCreditCard {
  type: 'CREDIT_CARD' | 'GOOGLE_PAY' | 'APPLE_PAY';
  cardNumberLastDigits?: string;
  cardBrand: CardBrand;
}

export interface BuyTransaction extends TransactionBase {
  type: TransactionType.BUY;
  paymentMethod: MaskedCreditCard;
  frequency?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  intergiroTransactionCode?: string;
  pspFee?: MoneyScaled;
}

export interface SellTransaction extends TransactionBase {
  type: TransactionType.SELL;
}

interface UserDestination {
  profileImage?: string;
  firstName?: string;
  lastName?: string;
}

interface MerchantDestination {
  uuid: string;
  name: string;
  userId: string;
  redirectUrl?: string;
  orderId?: string;
}

interface ContactDestination {
  uuid: string;
  label: string;
  isInternal: boolean;
}

export interface Destination {
  address: string;
  type: 'PERSON' | 'MERCHANT';
  user?: UserDestination;
  merchant?: MerchantDestination;
  contact?: ContactDestination;
}

interface SendTransaction {
  destination?: Destination;
}

export interface SendInternalTransaction
  extends TransactionBase,
    SendTransaction {
  type: TransactionType.SEND_INTERNAL;
}

export interface SendExternalTransaction
  extends TransactionBase,
    SendTransaction {
  type: TransactionType.SEND_EXTERNAL;
  blockchainTransactionId?: string;
  blockchainExplorerUrl?: string;
  networkFee: MoneyScaled;
  total: MoneyScaled;
  sourceAddress?: string;
  beneficiaryVaspName?: string;
  beneficiaryName?: string;
}

export interface ReceiveInternalTransaction extends TransactionBase {
  type: TransactionType.RECEIVE_INTERNAL;
  origin?: Destination;
}

export interface ReceiveExternalTransaction extends TransactionBase {
  type: TransactionType.RECEIVE_EXTERNAL;
  blockchainTransactionId?: string;
  blockchainExplorerUrl?: string;
  networkFee: MoneyScaled;
  total: MoneyScaled;
}

export interface SwapSourceTransaction extends TransactionBase {
  type: TransactionType.SWAP_SOURCE;
  swapId: string;
  destinationWalletId: string;
  destinationAmount: MoneyScaled;
  destinationToSourceExchangeRate: MoneyScaled;
}

export interface SwapDestinationTransaction extends TransactionBase {
  type: TransactionType.SWAP_DESTINATION;
  swapId: string;
  sourceWalletId: string;
  sourceAmount: MoneyScaled;
  sourceToDestinationExchangeRate: MoneyScaled;
}

export interface RefundTransaction extends TransactionBase {
  type: TransactionType.REFUND;
}

interface BankAccountAddress {
  name?: string;
  iban: string;
  bic: string;
}

export interface SendFiatInternalTransaction extends TransactionBase {
  type: TransactionType.SEND_FIAT_INTERNAL;
  destination: BankAccountAddress;
  destinationUser: UserDestination;
}

export interface SendFiatExternalTransaction extends TransactionBase {
  type: TransactionType.SEND_FIAT_EXTERNAL;
  destination: BankAccountAddress;
}

export interface ReceiveFiatInternalTransaction extends TransactionBase {
  type: TransactionType.RECEIVE_FIAT_INTERNAL;
  source: BankAccountAddress;
  sourceUser: UserDestination;
}

export interface ReceiveFiatExternalTransaction extends TransactionBase {
  type: TransactionType.RECEIVE_FIAT_EXTERNAL;
  sourceIban: string;
}

interface CardPaymentMerchant {
  name: string;
  location: string;
}

export interface CardPaymentTransaction extends TransactionBase {
  type: TransactionType.CARD_PAYMENT;
  amountPayment: MoneyScaled;
  exchangeRatePayment: MoneyScaled;
  cardId: string;
  merchant?: CardPaymentMerchant;
}

export interface InternalTransferSourceTransaction extends TransactionBase {
  type: TransactionType.INTERNAL_TRANSFER_SOURCE;
  destinationWalletId: string;
  internalTransferType: string;
}

export interface InternalTransferDestinationTransaction
  extends TransactionBase {
  type: TransactionType.INTERNAL_TRANSFER_DESTINATION;
  sourceWalletId: string;
  internalTransferType: string;
}

export interface YieldPayoutTransaction extends TransactionBase {
  type: TransactionType.YIELD_PAYOUT;
}

export interface GiveawayTransaction extends TransactionBase {
  type: TransactionType.GIVEAWAY;
}

export type Transaction =
  | BuyTransaction
  | SellTransaction
  | SendInternalTransaction
  | SendExternalTransaction
  | ReceiveInternalTransaction
  | ReceiveExternalTransaction
  | SwapSourceTransaction
  | SwapDestinationTransaction
  | RefundTransaction
  | SendFiatInternalTransaction
  | SendFiatExternalTransaction
  | ReceiveFiatInternalTransaction
  | ReceiveFiatExternalTransaction
  | CardPaymentTransaction
  | InternalTransferSourceTransaction
  | InternalTransferDestinationTransaction
  | YieldPayoutTransaction
  | GiveawayTransaction;
